(function () {
  "use strict";

  const DESKTOP = window.matchMedia("(min-width: 992px)");
  const root =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
    "/";

  /* ------------------------------
     Quick cart
     The theme's quick cart drawer listens on its own internal event bus.
     window.emit is that bus, but only the theme's header exposes it, so fall
     back to the DOM event the quick cart also listens for.
     ------------------------------ */
  function hasQuickCart() {
    return Boolean(document.querySelector(".quick-cart"));
  }

  function openQuickCart() {
    if (typeof window.emit === "function") {
      window.emit("quick-cart:open");
    } else {
      document.dispatchEvent(new CustomEvent("apps:product-added-to-cart"));
    }
  }

  /* ------------------------------
     Mega menu
     ------------------------------ */
  function initMega(sectionEl, signal) {
    const items = Array.from(
      sectionEl.querySelectorAll(".cwc_header__nav-item--has-mega"),
    );
    if (!items.length) return null;

    let openTimer = null;
    let closeTimer = null;
    let lastPointer = "mouse";
    let openItem = null;

    function setExpanded(item, expanded) {
      const link = item.querySelector(".cwc_header__nav-link");
      if (link) link.setAttribute("aria-expanded", expanded ? "true" : "false");
    }

    function close(item) {
      const target = item || openItem;
      if (!target) return;
      target.classList.remove("cwc_header__nav-item--open");
      setExpanded(target, false);
      if (openItem === target) openItem = null;
    }

    // Room left between the panel's top edge and the bottom of the screen. The
    // panel is laid out while hidden, so its position can be read before it shows.
    function fitPanel(item) {
      const panel = item.querySelector(".cwc_header__mega");
      if (!panel) return;
      const room = window.innerHeight - panel.getBoundingClientRect().top - 16;
      sectionEl.style.setProperty(
        "--hd-mega-max-height",
        Math.max(room, 240) + "px",
      );
    }

    function open(item) {
      if (openItem && openItem !== item) close(openItem);
      fitPanel(item);
      item.classList.add("cwc_header__nav-item--open");
      item.classList.remove("cwc_header__nav-item--suppressed");
      setExpanded(item, true);
      openItem = item;
    }

    function clearTimers() {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
    }

    items.forEach(function (item) {
      const link = item.querySelector(".cwc_header__nav-link");
      setExpanded(item, false);

      // Pointer events fire before the mouse events a tap emulates, so this
      // tells a real hover apart from a touch
      item.addEventListener(
        "pointerenter",
        function (event) {
          lastPointer = event.pointerType || "mouse";
        },
        { signal: signal },
      );

      // Small delays so sweeping the pointer across the nav doesn't flash panels
      item.addEventListener(
        "mouseenter",
        function () {
          if (lastPointer === "touch") return;
          clearTimers();
          openTimer = window.setTimeout(
            function () {
              open(item);
            },
            openItem ? 0 : 90,
          );
        },
        { signal: signal },
      );

      item.addEventListener(
        "mouseleave",
        function () {
          if (lastPointer === "touch") return;
          clearTimers();
          closeTimer = window.setTimeout(function () {
            close(item);
          }, 160);
        },
        { signal: signal },
      );

      // Touch: first tap opens the panel, second tap follows the link
      if (link) {
        link.addEventListener(
          "pointerdown",
          function (event) {
            lastPointer = event.pointerType || "mouse";
          },
          { signal: signal },
        );

        link.addEventListener(
          "click",
          function (event) {
            if (lastPointer !== "touch") return;
            if (!item.classList.contains("cwc_header__nav-item--open")) {
              event.preventDefault();
              open(item);
            }
          },
          { signal: signal },
        );
      }

      item.addEventListener(
        "focusout",
        function (event) {
          if (!item.contains(event.relatedTarget)) {
            close(item);
            item.classList.remove("cwc_header__nav-item--suppressed");
          }
        },
        { signal: signal },
      );

      item.addEventListener(
        "keydown",
        function (event) {
          if (event.key !== "Escape") return;
          close(item);
          // Keyboard focus would otherwise hold the panel open via :focus-visible
          item.classList.add("cwc_header__nav-item--suppressed");
          if (link) link.focus();
        },
        { signal: signal },
      );
    });

    document.addEventListener(
      "click",
      function (event) {
        if (openItem && !openItem.contains(event.target)) close(openItem);
      },
      { signal: signal },
    );

    window.addEventListener(
      "resize",
      function () {
        if (openItem) fitPanel(openItem);
      },
      { signal: signal },
    );

    // Theme editor: show the panel whose block is being edited
    sectionEl.addEventListener(
      "shopify:block:select",
      function (event) {
        const item = event.target.closest(".cwc_header__nav-item--has-mega");
        if (item) open(item);
      },
      { signal: signal },
    );

    sectionEl.addEventListener(
      "shopify:block:deselect",
      function () {
        close();
      },
      { signal: signal },
    );

    return {
      isOpen: function () {
        return Boolean(openItem);
      },
      refit: function () {
        if (openItem) fitPanel(openItem);
      },
      closeAll: function () {
        close();
      },
    };
  }

  /* ------------------------------
     Predictive search
     Runs on the desktop header search and on the mobile menu's search.
     ------------------------------ */
  function initSearch(wrapper, signal) {
    if (!wrapper) return null;

    const input = wrapper.querySelector("[data-cwc-search-input]");
    const results = wrapper.querySelector("[data-cwc-search-results]");
    const status = wrapper.querySelector("[data-cwc-search-status]");
    const form = wrapper.querySelector("form");
    if (!input || !results || !form || wrapper.dataset.predictive !== "true")
      return null;

    let debounce = null;
    let controller = null;

    input.setAttribute("aria-controls", results.id);
    input.setAttribute("aria-expanded", "false");

    function isOpen() {
      return !results.hidden;
    }

    function hide() {
      results.hidden = true;
      input.setAttribute("aria-expanded", "false");
    }

    function show() {
      results.hidden = false;
      input.setAttribute("aria-expanded", "true");
    }

    function el(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text != null) node.textContent = text;
      return node;
    }

    function formatMoney(value) {
      const amount = parseFloat(value);
      if (isNaN(amount)) return "";
      const currency =
        (window.Shopify &&
          window.Shopify.currency &&
          window.Shopify.currency.active) ||
        "USD";
      try {
        return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
          style: "currency",
          currency: currency,
        }).format(amount);
      } catch (e) {
        return amount.toFixed(2);
      }
    }

    function group(title, list) {
      const section = el("div", "cwc_header__search-group");
      section.appendChild(el("p", "cwc_header__search-group-title", title));
      const ul = el("ul", "cwc_header__search-list");
      list.forEach(function (node) {
        const li = el("li");
        li.appendChild(node);
        ul.appendChild(li);
      });
      section.appendChild(ul);
      return section;
    }

    function textItem(href, label) {
      const a = el("a", "cwc_header__search-item", label);
      a.href = href;
      return a;
    }

    function productItem(product) {
      const a = el("a", "cwc_header__search-item");
      a.href = product.url;

      const imageUrl =
        (product.featured_image && product.featured_image.url) || product.image;
      if (imageUrl) {
        const img = el("img", "cwc_header__search-thumb");
        img.src =
          imageUrl + (imageUrl.indexOf("?") > -1 ? "&" : "?") + "width=120";
        img.alt = "";
        img.width = 48;
        img.height = 48;
        img.loading = "lazy";
        a.appendChild(img);
      } else {
        a.appendChild(
          el(
            "span",
            "cwc_header__search-thumb cwc_header__search-thumb--empty",
          ),
        );
      }

      a.appendChild(el("span", "cwc_header__search-name", product.title));
      const price = formatMoney(product.price);
      if (price) a.appendChild(el("span", "cwc_header__search-price", price));
      return a;
    }

    function render(data, query) {
      const res = (data && data.resources && data.resources.results) || {};
      const queries = res.queries || [];
      // Store rule: products tagged no_feed (e.g. shipping protection) never show
      const products = (res.products || [])
        .filter(function (product) {
          const tags = (product.tags || []).map(function (tag) {
            return String(tag).toLowerCase();
          });
          return (
            tags.indexOf("no_feed") === -1 && tags.indexOf("no-feed") === -1
          );
        })
        .slice(0, 6);
      const collections = res.collections || [];

      results.textContent = "";

      if (queries.length) {
        results.appendChild(
          group(
            "Suggestions",
            queries.map(function (q) {
              return textItem(q.url, q.text);
            }),
          ),
        );
      }
      if (products.length) {
        results.appendChild(group("Bottles", products.map(productItem)));
      }
      if (collections.length) {
        results.appendChild(
          group(
            "Collections",
            collections.map(function (c) {
              return textItem(c.url, c.title);
            }),
          ),
        );
      }
      if (!queries.length && !products.length && !collections.length) {
        results.appendChild(
          el("p", "cwc_header__search-empty", 'No matches for "' + query + '"'),
        );
      }

      const all = el(
        "a",
        "cwc_header__search-all",
        'Search for "' + query + '" →',
      );
      all.href = form.action + "?q=" + encodeURIComponent(query);
      results.appendChild(all);

      if (status) status.textContent = products.length + " bottles found";
      show();
    }

    function search(query) {
      if (controller) controller.abort();
      controller = new AbortController();

      const params = new URLSearchParams();
      params.set("q", query);
      params.set("resources[type]", "query,product,collection");
      // A couple spare, since no_feed products are dropped after the fetch
      params.set("resources[limit]", "8");
      params.set("resources[limit_scope]", "each");
      params.set("resources[options][unavailable_products]", "last");

      fetch(root + "search/suggest.json?" + params.toString(), {
        signal: controller.signal,
      })
        .then(function (response) {
          if (!response.ok) throw new Error("Search failed");
          return response.json();
        })
        .then(function (data) {
          if (input.value.trim() === query) render(data, query);
        })
        .catch(function (error) {
          if (error.name !== "AbortError") hide();
        });
    }

    input.addEventListener(
      "input",
      function () {
        window.clearTimeout(debounce);
        const query = input.value.trim();
        if (query.length < 2) {
          if (controller) controller.abort();
          hide();
          return;
        }
        debounce = window.setTimeout(function () {
          search(query);
        }, 200);
      },
      { signal: signal },
    );

    input.addEventListener(
      "focus",
      function () {
        if (results.childElementCount && input.value.trim().length >= 2) show();
      },
      { signal: signal },
    );

    // Arrow keys move between the input and the result links
    wrapper.addEventListener(
      "keydown",
      function (event) {
        if (event.key === "Escape" && isOpen()) {
          // Close only the suggestions, not the mobile menu around them
          event.stopPropagation();
          hide();
          input.focus();
          return;
        }
        if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
        if (!isOpen()) return;

        const links = Array.from(results.querySelectorAll("a"));
        if (!links.length) return;
        event.preventDefault();

        const index = links.indexOf(document.activeElement);
        if (event.key === "ArrowDown") {
          links[index < links.length - 1 ? index + 1 : 0].focus();
        } else if (index <= 0) {
          input.focus();
        } else {
          links[index - 1].focus();
        }
      },
      { signal: signal },
    );

    // Safari doesn't focus a tapped link, so a tap on a result blurs the input
    // with no relatedTarget. Skip that blur; the document click handler below
    // still closes the panel for taps outside it.
    let pointerInside = false;
    wrapper.addEventListener(
      "pointerdown",
      function () {
        pointerInside = true;
        window.setTimeout(function () {
          pointerInside = false;
        }, 500);
      },
      { signal: signal },
    );

    wrapper.addEventListener(
      "focusout",
      function (event) {
        if (pointerInside) return;
        if (!wrapper.contains(event.relatedTarget)) hide();
      },
      { signal: signal },
    );

    document.addEventListener(
      "click",
      function (event) {
        if (!wrapper.contains(event.target)) hide();
      },
      { signal: signal },
    );

    return {
      isActive: function () {
        return isOpen() || document.activeElement === input;
      },
      hide: hide,
    };
  }

  /* ------------------------------
     Mobile menu
     ------------------------------ */
  function initMenu(sectionEl, signal) {
    const menu = document.getElementById(
      "cwc_header-menu-" + sectionEl.dataset.sectionId,
    );
    const openers = Array.from(
      sectionEl.querySelectorAll("[data-cwc-menu-open]"),
    );
    if (!menu || !openers.length) return null;

    const closeBtn = menu.querySelector("[data-cwc-menu-close]");
    const searchInput = menu.querySelector("[data-cwc-menu-search]");
    let lastOpener = null;
    let hideTimer = null;

    function focusables() {
      return Array.from(
        menu.querySelectorAll(
          'a[href], button, input, summary, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter(function (node) {
        return node.offsetParent !== null;
      });
    }

    function setExpanded(expanded) {
      openers.forEach(function (btn) {
        btn.setAttribute("aria-expanded", expanded ? "true" : "false");
      });
    }

    function open(opener) {
      window.clearTimeout(hideTimer);
      lastOpener = opener || null;
      menu.hidden = false;
      document.documentElement.classList.add("cwc-menu-locked");
      setExpanded(true);
      window.requestAnimationFrame(function () {
        menu.classList.add("cwc_header__menu--open");
      });

      const wantsSearch =
        opener && opener.hasAttribute("data-cwc-menu-search-focus");
      const target = wantsSearch && searchInput ? searchInput : closeBtn;
      if (target) target.focus({ preventScroll: true });
    }

    function close() {
      if (menu.hidden) return;
      menu.classList.remove("cwc_header__menu--open");
      document.documentElement.classList.remove("cwc-menu-locked");
      setExpanded(false);
      hideTimer = window.setTimeout(function () {
        menu.hidden = true;
      }, 250);
      if (lastOpener && DESKTOP.matches === false)
        lastOpener.focus({ preventScroll: true });
    }

    openers.forEach(function (btn) {
      btn.addEventListener(
        "click",
        function () {
          open(btn);
        },
        { signal: signal },
      );
    });

    if (closeBtn) closeBtn.addEventListener("click", close, { signal: signal });

    menu.addEventListener(
      "keydown",
      function (event) {
        if (event.key === "Escape") {
          close();
          return;
        }
        if (event.key !== "Tab") return;

        // Keep focus inside the open menu
        const nodes = focusables();
        if (!nodes.length) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      },
      { signal: signal },
    );

    DESKTOP.addEventListener(
      "change",
      function (event) {
        if (event.matches) close();
      },
      { signal: signal },
    );

    return { close: close };
  }

  /* ------------------------------
     Cart icon + count
     ------------------------------ */
  function initCart(sectionEl, menuApi, signal) {
    const menu = document.getElementById(
      "cwc_header-menu-" + sectionEl.dataset.sectionId,
    );
    const scopes = [sectionEl, menu].filter(Boolean);

    function triggers() {
      return scopes.reduce(function (list, scope) {
        return list.concat(
          Array.from(scope.querySelectorAll("[data-cwc-cart]")),
        );
      }, []);
    }

    function counts() {
      return scopes.reduce(function (list, scope) {
        return list.concat(
          Array.from(scope.querySelectorAll("[data-cwc-cart-count]")),
        );
      }, []);
    }

    function setCount(count) {
      if (typeof count !== "number") return;
      counts().forEach(function (node) {
        node.textContent = count;
        node.hidden = count === 0;
      });
      triggers().forEach(function (link) {
        link.setAttribute("aria-label", "Cart, " + count + " items");
      });
    }

    triggers().forEach(function (link) {
      link.addEventListener(
        "click",
        function (event) {
          if (!hasQuickCart() || window.location.pathname === link.pathname)
            return;
          event.preventDefault();
          if (menuApi) menuApi.close();
          openQuickCart();
        },
        { signal: signal },
      );
    });

    document.addEventListener(
      "cart:updated",
      function (event) {
        const cart = event.detail && event.detail.cart;
        if (cart) setCount(cart.item_count);
      },
      { signal: signal },
    );

    // Without the theme's own header nothing else opens the quick cart after an add
    document.addEventListener(
      "cart:item-added",
      function () {
        if (typeof window.emit !== "function" && hasQuickCart())
          openQuickCart();
      },
      { signal: signal },
    );

    // Back/forward cache can restore a stale count
    window.addEventListener(
      "pageshow",
      function (event) {
        if (!event.persisted) return;
        fetch(root + "cart.js")
          .then(function (response) {
            return response.json();
          })
          .then(function (cart) {
            setCount(cart.item_count);
          })
          .catch(function () {});
      },
      { signal: signal },
    );
  }

  /* ------------------------------
     Sticky height + hide on scroll
     ------------------------------ */
  function initScroll(sectionEl, mega, search, signal) {
    const top = sectionEl.querySelector("[data-cwc-header-top]");
    const nav = sectionEl.querySelector(".cwc_header__nav");
    const sticky = sectionEl.dataset.sticky === "true";
    const hideOnScroll =
      sticky && sectionEl.dataset.hideOnScroll === "true" && Boolean(nav);
    // hide_top: the top bar slides away. hide_nav: the menu row tucks under the top bar
    const hideNav = sectionEl.dataset.scrollMode === "hide_nav";
    const navToggle = sectionEl.querySelector("[data-cwc-nav-toggle]");
    const docStyle = document.documentElement.style;

    let lastY = window.scrollY;
    // How far the shopper has scrolled up in one go; small trackpad jitters don't count
    let upTravel = 0;
    const REVEAL_AFTER = 36;
    // Scroll depth (px) before the header is allowed to shrink
    const HIDE_AFTER = 8;
    let ticking = false;

    // Other sections (sticky filter bars, gallery) sit below whatever part of the header is showing
    function publishHeight() {
      let height = 0;
      if (sticky) {
        height = sectionEl.offsetHeight;
        if (sectionEl.classList.contains("cwc_header--compact")) {
          if (hideNav) {
            if (!sectionEl.classList.contains("cwc_header--nav-open"))
              height -= nav.offsetHeight;
          } else if (top) {
            height -= top.offsetHeight;
          }
        }
      }
      docStyle.setProperty("--cwc-header-height", height + "px");
      docStyle.setProperty("--header-desktop-sticky-height", height + "px");
    }

    function measure() {
      if (top)
        sectionEl.style.setProperty("--hd-top-height", top.offsetHeight + "px");
      publishHeight();
    }

    function setNavOpen(navOpen) {
      if (
        !hideNav ||
        sectionEl.classList.contains("cwc_header--nav-open") === navOpen
      )
        return;
      sectionEl.classList.toggle("cwc_header--nav-open", navOpen);
      if (navToggle) {
        navToggle.setAttribute("aria-expanded", navOpen ? "true" : "false");
        navToggle.setAttribute(
          "aria-label",
          navOpen ? "Hide menu" : "Show menu",
        );
      }
      if (!navOpen && mega) mega.closeAll();
      publishHeight();
    }

    function setCompact(compact) {
      if (sectionEl.classList.contains("cwc_header--compact") === compact)
        return;
      if (!compact) setNavOpen(false);
      sectionEl.classList.toggle("cwc_header--compact", compact);
      const navCart = sectionEl.querySelector(".cwc_header__nav-cart");
      if (navCart) navCart.setAttribute("tabindex", compact ? "0" : "-1");
      publishHeight();
    }

    function onScroll() {
      ticking = false;
      const y = Math.max(window.scrollY, 0);
      const delta = y - lastY;
      lastY = y;

      if (!hideOnScroll || !DESKTOP.matches) {
        setCompact(false);
        return;
      }

      // Hold still while the shopper is using the menu or search
      if ((mega && mega.isOpen()) || (search && search.isActive())) return;

      // Only the very top of the page keeps the full header; a short deliberate
      // scroll down past it tucks the row away
      if (delta > 0) upTravel = 0;
      else if (delta < 0) upTravel -= delta;

      if (y <= HIDE_AFTER) {
        setCompact(false);
      } else if (delta > 1) {
        setCompact(true);
        setNavOpen(false);
      } else if (upTravel > REVEAL_AFTER) {
        // Heading back up: bring back whichever row was hidden. Keep-logo mode
        // stays shrunk, so the menu button is still there to tuck the row away
        if (hideNav) setNavOpen(true);
        else setCompact(false);
      }
    }

    // A panel opened while a row was still sliding was measured mid-move
    sectionEl.addEventListener(
      "transitionend",
      function (event) {
        if (
          (event.target === sectionEl || event.target === nav) &&
          event.propertyName === "transform" &&
          mega
        )
          mega.refit();
      },
      { signal: signal },
    );

    if (navToggle) {
      navToggle.addEventListener(
        "click",
        function () {
          setNavOpen(!sectionEl.classList.contains("cwc_header--nav-open"));
        },
        { signal: signal },
      );
    }

    window.addEventListener(
      "scroll",
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(onScroll);
      },
      { passive: true, signal: signal },
    );

    if ("ResizeObserver" in window) {
      const observer = new ResizeObserver(measure);
      observer.observe(sectionEl);
      signal.addEventListener("abort", function () {
        observer.disconnect();
      });
    }

    DESKTOP.addEventListener(
      "change",
      function () {
        setCompact(false);
        measure();
      },
      { signal: signal },
    );

    measure();
  }

  /* ------------------------------
     Section init
     ------------------------------ */
  function initSection(sectionEl) {
    if (!sectionEl || sectionEl.cwcController) return;

    const controller = new AbortController();
    const signal = controller.signal;
    sectionEl.cwcController = controller;
    sectionEl.classList.add("cwc_header--js");

    const mega = initMega(sectionEl, signal);
    const search = initSearch(
      sectionEl.querySelector("[data-cwc-search]"),
      signal,
    );
    const menu = initMenu(sectionEl, signal);
    const menuEl = document.getElementById(
      "cwc_header-menu-" + sectionEl.dataset.sectionId,
    );
    if (menuEl) initSearch(menuEl.querySelector("[data-cwc-search]"), signal);
    initCart(sectionEl, menu, signal);
    initScroll(sectionEl, mega, search, signal);
  }

  function initAllSections() {
    document.querySelectorAll(".cwc_header").forEach(initSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAllSections);
  } else {
    initAllSections();
  }

  document.addEventListener("shopify:section:load", function (event) {
    const section = event.target.querySelector(".cwc_header");
    if (section) initSection(section);
  });

  // Theme editor: drop the old instance's document and window listeners
  document.addEventListener("shopify:section:unload", function (event) {
    const section = event.target.querySelector(".cwc_header");
    if (section && section.cwcController) {
      section.cwcController.abort();
      document.documentElement.classList.remove("cwc-menu-locked");
    }
  });
})();
